/**
 * Golf-term resolver shared by Score entry + Scorecard. Gross-based per the
 * Slice B correction: a 3 on a par-3 is always "Par", regardless of strokes
 * given from handicap.
 *
 * Tone tokens map to Tailwind class bundles; consumers spread them onto the
 * decorated element.
 */
/**
 * Seven-tier tone grammar matching the broadcast/TV scorecard
 * convention:
 *
 *   albatross  ≤ par − 3   (HiO on par 4+; double-eagle; rare)
 *   eagle      par − 2
 *   birdie     par − 1
 *   par        par
 *   bogey      par + 1
 *   double     par + 2
 *   triple     ≥ par + 3   (triple, quad, quintuple, …)
 *
 * Filled vs hollow + outer-bordered tiers are the Scorecard's visual
 * distinction — see Scorecard.tsx for the marks.
 */
export type Tone =
  | 'albatross'
  | 'eagle'
  | 'birdie'
  | 'par'
  | 'bogey'
  | 'double'
  | 'triple'

export type Term = { label: string; tone: Tone }

export const termFor = (gross: number, par: number): Term => {
  if (gross === 1)
    return { label: 'HiO', tone: par >= 4 ? 'albatross' : 'eagle' }
  const diff = gross - par
  if (diff <= -3) return { label: 'Albatross', tone: 'albatross' }
  if (diff === -2) return { label: 'Eagle', tone: 'eagle' }
  if (diff === -1) return { label: 'Birdie', tone: 'birdie' }
  if (diff === 0) return { label: 'Par', tone: 'par' }
  if (diff === 1) return { label: 'Bogey', tone: 'bogey' }
  if (diff === 2) return { label: 'Dobbel', tone: 'double' }
  return { label: `+${diff}`, tone: 'triple' }
}

/**
 * A net score phrased for an inline "netto …" clause: `netto birdie`.
 *
 * A net score is spoken as a term, not a number — "par, netto birdie", never
 * "par netto 3". The raw figure says nothing a golfer thinks in.
 *
 * Deliberately NOT `termFor`: that reads a 1 as "HiO", which is a claim about
 * the ball going in off the tee. A net 1 is nothing of the sort — on a par 4 it
 * is a net albatross — so the net term is purely par-relative. The `+N` tiers
 * have no word and stay as they are.
 */
export const netTermLabel = (net: number, par: number): string => {
  const diff = net - par
  if (diff <= -3) return 'albatross'
  if (diff === -2) return 'eagle'
  if (diff === -1) return 'birdie'
  if (diff === 0) return 'par'
  if (diff === 1) return 'bogey'
  if (diff === 2) return 'dobbel'
  return `+${diff}`
}

export const TONE_STYLES: Record<
  Tone,
  { bg: string; fg: string; ring: string }
> = {
  // Albatross and Triple alias to their adjacent tier for the
  // Score-entry button picker (the visual difference only matters on
  // the Scorecard). Buttons stay readable; the Scorecard mark library
  // carries the seven-tier grammar.
  albatross: { bg: 'bg-gold/15', fg: 'text-gold', ring: 'ring-gold' },
  eagle: { bg: 'bg-gold/15', fg: 'text-gold', ring: 'ring-gold' },
  birdie: { bg: 'bg-green-100', fg: 'text-green-700', ring: 'ring-green-500' },
  par: { bg: 'bg-ink-100', fg: 'text-ink-700', ring: 'ring-ink-400' },
  bogey: { bg: 'bg-warn/20', fg: 'text-warn', ring: 'ring-warn' },
  double: { bg: 'bg-flag/15', fg: 'text-flag', ring: 'ring-flag' },
  triple: { bg: 'bg-flag/15', fg: 'text-flag', ring: 'ring-flag' },
}
